import { toast } from "sonner";

import type { Listing, ListingStatus } from "@/.zenstack/logical-prisma-client";
import {
  ListingStateMachine,
  type ListingAction,
  type ListingForStateMachine,
} from "@/lib/listing/listing-state-machine";

export const BedroomOptions = [
  { value: "Studio", label: "Studio" },
  { value: "1", label: "1 Bedroom" },
  { value: "2", label: "2 Bedrooms" },
  { value: "3", label: "3 Bedrooms" },
  { value: "4", label: "4 Bedrooms" },
  { value: "5", label: "5 Bedrooms" },
  { value: "6+", label: "6 or more" },
] as const;

export type BedroomValue = (typeof BedroomOptions)[number]["value"];
export const bedroomRegex = /^(Studio|[1-5]|6\+)$/;

export const getBedroomLabel = (value: string): string => {
  const option = BedroomOptions.find((opt) => opt.value === value);
  return option ? option.label : value;
};

/**
 * Handles listing state transitions with validation and error handling.
 * This is a shared utility used by both ListingEditor and ListingMenuActions.
 *
 * @param action - The state transition action to perform (e.g., "SUBMIT", "APPROVE")
 * @param listing - The listing object to transition
 * @param updateListing - The mutation function from useUpdateListing hook
 * @param onEditClick - Optional callback when user clicks "Edit" button in error toast
 * @param successMessage - Optional custom success message (defaults to "Listing updated successfully")
 * @returns Promise<boolean> - true if transition succeeded, false otherwise
 */
export async function handleListingStateTransition(
  action: ListingAction,
  listing: Partial<Listing> & {
    id: string;
    status: ListingStatus;
    slug?: string | null;
  } & Pick<Listing, "createdById" | "updatedById" | "businessId">,
  updateListing: {
    mutateAsync: (args: {
      where: { id: string };
      data: { status: ListingStatus };
    }) => Promise<unknown>;
  },
  onEditClick?: () => void,
  successMessage = "Listing updated successfully",
): Promise<boolean> {
  const stateMachine = new ListingStateMachine();

  // Validate the state transition
  const { valid, errors } = stateMachine.canTransition(
    listing.status,
    action,
    listing as ListingForStateMachine,
  );

  if (!valid) {
    // Show user-friendly error with action buttons
    toast("Listing requires updates", {
      description: "Please complete all required fields before submitting.",
      duration: 10000, // Longer duration for actionable toast
      action: onEditClick
        ? {
            label: "Edit",
            onClick: onEditClick,
          }
        : undefined,
    });

    // Log specific validation errors for debugging
    if (errors.length > 0) {
      console.warn("Listing validation failed:", errors);
    }

    return false;
  }

  // Get the next state
  const nextState = stateMachine.getNextState(listing.status, action);

  try {
    await updateListing.mutateAsync({
      where: { id: listing.id },
      data: { status: nextState },
    });

    toast(successMessage);
    return true;
  } catch (error) {
    console.error("Error updating listing:", error);
    toast("Failed to update listing");
    return false;
  }
}
